import { useEffect, useState, useRef } from "react";
import { createPortal } from "react-dom";
import api from "@services/api";
import Modal from "@components/utils/Modal";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import Logo from "../../assets/icon.png";
import { setUser, setOrganization } from "../../redux/auth/actions";
import LoadingButton from "@components/utils/LoadingButton";
import { phoneIndicatifs } from "../../constants";
import { formatPhoneNumberAsYouType, validateAndFormatPhoneNumber, getCountryCodeFromIndicatif, cleanPhoneNumber } from "../../utils/index";
import toast from "react-hot-toast";

const MissingInfosModal = () => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const { user, organization } = useSelector((state) => state.Auth);
  const [userValues, setUserValues] = useState({
    indicatif: "+33",
    phone: user?.phone ? user.phone.replace(/^\+\d+/, "") : "",
  });
  const [organizationValues, setOrganizationValues] = useState({
    business: organization?.business,
    activity: organization?.activity,
  });
  const [phoneValidation, setPhoneValidation] = useState({ isValid: false, error: null });
  const dispatch = useDispatch();

  // Validation du numéro de téléphone en temps réel
  useEffect(() => {
    if (userValues.phone) {
      const countryCode = getCountryCodeFromIndicatif(userValues.indicatif);
      const fullNumber = userValues.indicatif + userValues.phone;
      const validation = validateAndFormatPhoneNumber(fullNumber, countryCode);
      setPhoneValidation(validation);
    } else {
      setPhoneValidation({ isValid: false, error: null });
    }
  }, [userValues.phone, userValues.indicatif]);

  const submit = async () => {
    try {
      setLoading(true);

      if (!user.phone && user.phone !== "") {
        // Validation finale avant soumission
        if (userValues.phone && !phoneValidation.isValid) {
          toast.error("Format de numéro de téléphone invalide");
          setLoading(false);
          return;
        }

        // Utiliser le format E.164 pour le stockage du téléphone
        const phoneToSave = phoneValidation.isValid && phoneValidation.e164Format ? phoneValidation.e164Format : `${userValues.indicatif}${userValues.phone}`;

        const { ok, data } = await api.put(`/user/${user._id}`, {
          ...userValues,
          phone: phoneToSave,
        });

        if (!ok) {
          setLoading(false);
          return toast.error(t("toast.error.updating_profile"));
        }
        dispatch(setUser(data));
      }

      if (!organization.business && organization.business !== "") {
        const { ok, data } = await api.put(`/organization/${organization._id}`, { ...organizationValues });
        if (!ok) {
          setLoading(false);
          return toast.error(t("toast.error.updating_organization"));
        }
        dispatch(setOrganization(data));
      }

      toast.success(t("toast.sucess.updated_profile"));
      setOpen(false);
    } catch (e) {
      toast.error(t("toast.error.updating_profile"));
    } finally {
      setLoading(false);
    }
  };

  const handleDisplayMissingInfos = async () => {
    try {
      setOpen(false);
      if (!user.phone || !organization.business) setOpen(true);
    } catch (e) {
      console.log(e);
    }
  };

  useEffect(() => {
    handleDisplayMissingInfos();
  }, [user, organization]);

  const isDisabled = () => {
    const isUserPhoneValid = userValues.phone && userValues.phone !== "" && phoneValidation.isValid;
    const isOrganizationBusinessValid = organizationValues.business && organizationValues.business !== "";
    const needPhone = !user.phone;
    const needBusiness = !organization.business;
    return (needPhone && !isUserPhoneValid) || (needBusiness && !isOrganizationBusinessValid);
  };

  if (!user || !organization) return null;

  return (
    <Modal isOpen={open} onClose={() => setOpen(false)} innerClassName="flex max-w-[40rem] w-[90vw] max-h-[calc(100vh-5rem)] ">
      <div className="w-full max-h-full flex flex-col overflow-y-auto transform rounded-2xl py-4 px-8 bg-white text-left align-middle shadow-xl transition-all">
        <div className="flex justify-center mb-4">
          <img src={Logo} alt="Logo" className="h-12" />
        </div>
        <div className="mb-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-2">{t("missing_information")}</h2>
          <p className="text-gray-600">{t("missing_information.description_1")}</p>
          <p className="text-gray-600 mt-2">{t("missing_information.description_2")}</p>
        </div>
        <div className="flex flex-col gap-4">
          {!user.phone && <Phone values={userValues} setValues={setUserValues} phoneValidation={phoneValidation} />}
          {!organization.business && <BusinessActivity values={organizationValues} setValues={setOrganizationValues} />}
        </div>
        <div className="flex justify-end items-center mt-auto pt-8">
          <div className="relative group">
            <LoadingButton
              type="submit"
              onClick={submit}
              loading={loading}
              disabled={isDisabled()}
              className="bg-blue-500 hover:bg-blue-600 text-white px-6 py-3 rounded-lg font-medium transition-all duration-200 flex items-center justify-center min-w-[140px] shadow-lg shadow-blue-200/50 disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none">
              {t("continue")}
            </LoadingButton>
            {isDisabled() && (
              <div className="absolute bottom-full right-0 mb-2 w-fit min-w-[190px] bg-gray-800 text-white text-sm rounded-lg p-3 shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 pointer-events-none">
                {!userValues.phone && !user.phone && <p>{t("onboarding.first_step.phone_required")}</p>}
                {userValues.phone && !phoneValidation.isValid && <p>{t("phone.invalid_format")}</p>}
                {!organizationValues.business && <p>{t("organization.business_activity_missing")}</p>}
                {organizationValues.business === "other" && !organizationValues.activity && <p>{t("organization.activity_missing")}</p>}
              </div>
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
};

const BusinessActivity = ({ values, setValues }) => {
  const { t } = useTranslation();

  return (
    <div className="flex flex-col col-span-2">
      <label className="text-sm font-medium text-gray-700 mb-1">
        {t("organization.activity")}
        <span className="text-red-500 ml-1">*</span>
      </label>
      <div className="relative mb-1">
        <select
          className="w-full appearance-none px-4 py-2 rounded-lg bg-white border border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 focus:ring-opacity-50 outline-none transition-all duration-200"
          value={values.business || ""}
          onChange={(e) => setValues({ ...values, business: e.target.value })}>
          <option value="">{t("select_activity")}</option>
          <option value="coach">{t("business_activities.coach")}</option>
          <option value="ecommerce">{t("business_activities.ecommerce")}</option>
          <option value="freelance">{t("business_activities.freelance")}</option>
          <option value="other">{t("others")}</option>
        </select>
        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-4 text-gray-500">
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
          </svg>
        </div>
      </div>
      <textarea
        id="activity"
        rows={3}
        className="px-4 py-2 rounded-lg bg-white border border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 focus:ring-opacity-50 outline-none transition-all duration-200"
        value={values?.activity ?? ""}
        onChange={(e) => setValues({ ...values, activity: e.target.value })}
        placeholder={t("organization.activity_placeholder")}
      />
    </div>
  );
};

const Phone = ({ values, setValues, phoneValidation }) => {
  const { t } = useTranslation();
  // Ajouter une référence pour l'input de téléphone
  const phoneInputRef = useRef(null);
  // État pour gérer la position du curseur
  const [cursorPosition, setCursorPosition] = useState(null);

  // Restaurer la position du curseur après la mise à jour
  useEffect(() => {
    if (cursorPosition !== null && phoneInputRef.current) {
      phoneInputRef.current.setSelectionRange(cursorPosition, cursorPosition);
      setCursorPosition(null);
    }
  }, [values.phone, cursorPosition]);

  const handlePhoneChange = (e) => {
    const input = e.target.value;
    const currentCursorPosition = e.target.selectionStart;
    const countryCode = getCountryCodeFromIndicatif(values.indicatif);

    // Compter les caractères non-numériques avant la position du curseur
    const textBeforeCursor = input.substring(0, currentCursorPosition);
    const digitsBeforeCursor = textBeforeCursor.replace(/[^\d]/g, "").length;

    // Nettoyer l'input en gardant seulement les chiffres et espaces
    const cleanedInput = cleanPhoneNumber(input);

    // Formater le numéro en temps réel
    const formattedNumber = formatPhoneNumberAsYouType(cleanedInput, countryCode);

    // Extraire seulement les chiffres pour le stockage
    const digitsOnly = formattedNumber.replace(/[^\d]/g, "");

    // Calculer la nouvelle position du curseur
    let newCursorPosition = 0;
    let digitCount = 0;
    for (let i = 0; i < formattedNumber.length; i++) {
      if (/\d/.test(formattedNumber[i])) {
        digitCount++;
        if (digitCount <= digitsBeforeCursor) {
          newCursorPosition = i + 1;
        } else {
          break;
        }
      } else if (digitCount < digitsBeforeCursor) {
        newCursorPosition = i + 1;
      }
    }

    // Sauvegarder la position pour la restaurer après le rendu
    setCursorPosition(newCursorPosition);
    setValues({ ...values, phone: digitsOnly });
  };

  const handleSuggestionClick = (suggestion) => {
    setValues({ ...values, phone: suggestion.number });
  };

  // Fonction pour afficher le numéro formaté dans l'input
  const getDisplayPhoneNumber = () => {
    if (!values.phone) return "";
    const countryCode = getCountryCodeFromIndicatif(values.indicatif);
    return formatPhoneNumberAsYouType(values.phone, countryCode);
  };

  const selectedIndicatif = phoneIndicatifs.find((i) => i.id === values.indicatif);

  return (
    <div className="flex flex-col">
      <label className="text-sm font-medium text-gray-700 mb-1">
        {t("phone_number")}
        <span className="text-red-500 ml-1">*</span>
      </label>
      <div className="flex gap-2 w-full">
        <CountrySelector selectedIndicatif={selectedIndicatif} onSelect={(indicatif) => setValues({ ...values, indicatif: indicatif.id })} />
        <div className="flex-1 relative">
          <input
            ref={phoneInputRef}
            className={`w-full px-4 py-2 rounded-lg bg-white outline-none border transition-all ${
              values.phone && !phoneValidation.isValid
                ? "border-red-300 focus:ring-red-200 focus:border-red-500"
                : values.phone && phoneValidation.isValid
                ? "border-green-300 focus:ring-green-200 focus:border-green-500"
                : "border-gray-200 focus:ring-blue-200 focus:border-blue-500"
            }`}
            value={getDisplayPhoneNumber()}
            onChange={handlePhoneChange}
            placeholder={selectedIndicatif.placeholder}
            type="tel"
            inputMode="tel"
            autoComplete="tel"
          />
          {/* Indicateur de validation avec bouton d'aide */}
          <div className="absolute inset-y-0 right-0 flex items-center pr-3 gap-2">
            {values.phone && (
              <div>
                {phoneValidation.isValid ? (
                  <svg className="w-5 h-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                ) : (
                  <svg className="w-5 h-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Message d'erreur et suggestions */}
      {values.phone && phoneValidation.error && (
        <div className="mt-2">
          <p className="text-red-500 text-sm">{phoneValidation.error}</p>

          {/* Suggestions d'amélioration */}
          {phoneValidation.suggestions && phoneValidation.suggestions.length > 0 && (
            <div className="mt-2 p-3 bg-blue-50 rounded-lg border border-blue-200">
              <p className="text-blue-700 text-sm font-medium mb-2">{t("phone.suggestions")}:</p>
              {phoneValidation.suggestions.map((suggestion, index) => (
                <button
                  key={index}
                  type="button"
                  onClick={() => handleSuggestionClick(suggestion)}
                  className="block w-full text-left text-sm text-blue-600 hover:text-blue-800 hover:bg-blue-100 p-2 rounded transition-colors">
                  {suggestion.description}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Aperçu du format international */}
      {values.phone && phoneValidation.isValid && phoneValidation.internationalNumber && (
        <p className="text-green-600 text-sm mt-1">
          {t("phone.international_format")}: {phoneValidation.internationalNumber}
        </p>
      )}
    </div>
  );
};

// Nouveau composant CountrySelector avec createPortal
const CountrySelector = ({ selectedIndicatif, onSelect }) => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  const buttonRef = useRef(null);
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);
  const { user } = useSelector((state) => state.Auth);
  const { t } = useTranslation();

  // Calculer la position de la liste déroulante
  const calculateDropdownPosition = () => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + 4,
        left: rect.left,
        width: rect.width,
      });
    }
  };

  // Fermer la dropdown si on clique en dehors
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target) && buttonRef.current && !buttonRef.current.contains(event.target)) {
        setDropdownOpen(false);
        setSearchQuery("");
      }
    };

    if (dropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      document.addEventListener("touchstart", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, [dropdownOpen]);

  // Recalculer la position si la fenêtre change de taille
  useEffect(() => {
    const handleResize = () => {
      if (dropdownOpen) {
        calculateDropdownPosition();
      }
    };

    if (dropdownOpen) {
      window.addEventListener("resize", handleResize);
      window.addEventListener("scroll", handleResize);
    }

    return () => {
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("scroll", handleResize);
    };
  }, [dropdownOpen]);

  const handleToggleDropdown = () => {
    const newDropdownState = !dropdownOpen;
    setDropdownOpen(newDropdownState);
    if (newDropdownState) {
      calculateDropdownPosition();
      setTimeout(() => searchInputRef.current?.focus(), 0);
    }
  };

  const handleSelectCountry = (indicatif) => {
    onSelect(indicatif);
    setDropdownOpen(false);
    setSearchQuery("");
  };

  const filteredIndicatifs = phoneIndicatifs.filter((indicatif) => {
    const label = `${indicatif.id} ${indicatif.flag} ${user.language === "FR" ? indicatif.countryNameFr : indicatif.countryNameEn}`.toLowerCase();
    return label.includes(searchQuery.toLowerCase());
  });

  const dropdownContent = dropdownOpen && (
    <div
      ref={dropdownRef}
      className="fixed z-[9999] bg-white border border-gray-200 rounded-lg shadow-lg animate-fade-in"
      style={{
        top: `${dropdownPosition.top}px`,
        left: `${dropdownPosition.left}px`,
        width: `${dropdownPosition.width}px`,
      }}>
      <input
        ref={searchInputRef}
        type="text"
        className="w-full px-3 py-2 border-b border-gray-200 text-sm outline-none focus:ring-1 focus:ring-blue-500"
        placeholder={t("search_country")}
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
      />
      <ul className="max-h-60 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent">
        {filteredIndicatifs.map((indicatif) => (
          <li
            key={indicatif.id}
            onClick={() => handleSelectCountry(indicatif)}
            className="px-3 py-2 cursor-pointer hover:bg-blue-50 text-sm flex items-center gap-2 transition-colors">
            <span>{indicatif.flag}</span>
            <span>{indicatif.id}</span>
            <span className="ml-auto text-gray-600">{user.language === "FR" ? indicatif.countryNameFr : indicatif.countryNameEn}</span>
          </li>
        ))}
      </ul>
    </div>
  );

  return (
    <div className="relative w-fit min-w-[120px]">
      <button
        ref={buttonRef}
        type="button"
        className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg flex items-center justify-between gap-1 focus:ring-2 focus:ring-blue-200 focus:outline-none"
        onClick={handleToggleDropdown}>
        <span className="flex items-center gap-2">
          <span>{selectedIndicatif.flag}</span>
          <span>{selectedIndicatif.id}</span>
          <span className="ml-auto text-gray-600">{user.language === "FR" ? selectedIndicatif.countryNameFr : selectedIndicatif.countryNameEn}</span>
        </span>
        <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
        </svg>
      </button>
      {dropdownContent && createPortal(dropdownContent, document.body)}
    </div>
  );
};

export default MissingInfosModal;
